import { EChartsOption } from "echarts";
import { Box, Container, Grid } from "@mui/material";
import { RopChart } from "../../../../main/components/RopChart";
import CardBox from "../../../../main/components/CardBox";
import { EmptyTable } from "../../../clients/components/EmptyState";
import {
  EnergiaGeradaInversoresMediaHoraria,
  EnergiaGeradaInversoresMes,
  EnergiaGeradaInversoresHoraChartData,
  EnergiaGeradaInversoresDiaChartData,
} from "../../types";
import { isNumberNullOrNegative } from "../../../../main/utils/utils";

export const colorList = Object.freeze([
  "#ee7d00",
  "#fab900",
  "#fade0a",
  "#5470c6",
  "#91cc75",
  "#fac858",
  "#ee6666",
  "#73c0de",
  "#3ba272",
  "#fc8452",
  "#9a60b4",
  "#ea7ccc",
  "#1A0144",
  "#6D0DB8",
]);

export function generateColorArray(numRegisters: number): string[] {
  const baseColors = ["#EE7D00", "#FAB900", "#FADE0A"];
  const result: string[] = [];

  if (numRegisters <= 0) {
    return result;
  }
  result.push(baseColors[0]);

  const mixRatio = 1 / (numRegisters - 1);

  for (let i = 1; i < numRegisters - 1; i++) {
    const mixAmount = i * mixRatio;
    const mixedColor = mixColors(baseColors[0], baseColors[2], mixAmount);
    result.push(mixedColor);
  }
  result.push(baseColors[2]);

  return result;
}

function mixColors(color1: string, color2: string, amount: number): string {
  const hex = (c: string) => parseInt(c, 16);
  const r = Math.round(
    hex(color1.slice(1, 3)) * (1 - amount) + hex(color2.slice(1, 3)) * amount
  );
  const g = Math.round(
    hex(color1.slice(3, 5)) * (1 - amount) + hex(color2.slice(3, 5)) * amount
  );
  const b = Math.round(
    hex(color1.slice(5, 7)) * (1 - amount) + hex(color2.slice(5, 7)) * amount
  );

  return `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

export function energiaGeradaInversoresChartDataFromEnergiaGeradaInversores(
  data: EnergiaGeradaInversoresMediaHoraria | EnergiaGeradaInversoresMes
): EnergiaGeradaInversoresHoraChartData | EnergiaGeradaInversoresDiaChartData {
  if ("horas" in data) {
    return {
      tipoFaixa: "hora",
      listaPerformanceUnidadeGeradora: data.listaPerformanceUnidadeGeradora.map(
        (item) => ({
          ug: item.ug,
          energiaGeradaMWh: item.energiaGeradaMWhDia,
          name: item.name,
        })
      ),
      listaEnergiaGeradaInversor: data.listaEnergiaGeradaInversor.map(
        (item) => ({
          ug: item.ug,
          inversor: item.inversor,
          energiaGeradaMWhPorFaixa: item.energiaGeradaMWhPorHora,
          name: item.name,
        })
      ),
      faixa: data.horas,
    };
  }

  return {
    tipoFaixa: "dia",
    faixa: data.dias,
    mes: data.month,
    ano: data.year,
    listaPerformanceUnidadeGeradora: data.listaPerformanceUnidadeGeradora.map(
      (item) => ({
        ug: item.ug,
        energiaGeradaMWh: item.energiaGeradaMWhDia,
        name: item.name,
      })
    ),
    listaEnergiaGeradaInversor: data.listaEnergiaGeradaInversor.map((item) => ({
      ug: item.ug,
      inversor: item.inversor,
      energiaGeradaMWhPorFaixa: item.energiaGeradaMWhDia,
      name: item.name,
    })),
  };
}

export default function GeneratedEnergyCharts(
  data: EnergiaGeradaInversoresMediaHoraria | EnergiaGeradaInversoresMes,

) {
  const energiaGeradaInversoresChartData =
    energiaGeradaInversoresChartDataFromEnergiaGeradaInversores(data);

  const { faixa } = energiaGeradaInversoresChartData;

  const faixaText = faixa.map((f) => {
    if (energiaGeradaInversoresChartData.tipoFaixa === "dia") {
      return `${f}/${energiaGeradaInversoresChartData.mes}`;
    }
    return `${f}h`;
  });

  const ugs: number[] =
    energiaGeradaInversoresChartData.listaPerformanceUnidadeGeradora.map(
      (item) => item.ug
    ) ?? [];

  const ugsInversorsSeries: {
    ug: number;
    type: "bar";
    name: string;
    data: number[];
    tooltip: {
      show: boolean;
      valueFormatter: (value: any) => string;
    };
  }[][] = ugs.map((ug) => {
    return (
      energiaGeradaInversoresChartData.listaEnergiaGeradaInversor
        .filter((item) => item.ug === ug)
        .map((data) => {
          const energiaGeradaArray = Object.values(
            data.energiaGeradaMWhPorFaixa
          );
          return {
            ug: data.ug,
            type: "bar" as const,
            name: data.name,
            data: energiaGeradaArray,
            tooltip: {
              show: true,
              valueFormatter: function (value: any) {
                return Number(value).toFixed(2);
              },
            },
          };
        }) ?? []
    );
  });

  const irradiacaoRealizadaArray = faixa.map(
    (hora) => {
      const value = data.irradiacaoRealizadaDia ? data.irradiacaoRealizadaDia[hora] : 0;
      return isNumberNullOrNegative(value ?? 0) ? '-' : Number(value);
    }
  );

  const ugsAverageSeries: {
    type: "line";
    name: string;
    color: string;
    data: (number | "-")[];
    yAxisIndex: number,
    tooltip: {
      show: boolean;
      valueFormatter: (value: any) => string;
    };
  } =
  {
    type: "line" as const,
    name: "Irradiação Realizada (kW/m²)",
    data: irradiacaoRealizadaArray,
    yAxisIndex: 1,
    color: "#6D0DB8",
    tooltip: {
      show: true,
      valueFormatter: function (value: any) {
        return Number(value).toFixed(2);
      },
    },
  }

  const ugsOptions: EChartsOption[] = ugsInversorsSeries.map((series) => {
    return {
      title: {
        text: `Energia Gerada (MWh) - UG ${series[0]?.ug}`,
        textStyle: {
          fontSize: 12,
        },
      },
      color: generateColorArray(series.length),
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
      backgroundColor: "transparent",
      legend: {
        itemWidth: 8,
        itemHeight: 8,
        textStyle: {
          fontSize: 13,
        },
      },

      grid: {
        left: 0,
        right: 0,
        bottom: 0,
        // top: 10,
        containLabel: true,
      },

      xAxis: {
        type: "category",
        data: faixaText,

        axisLabel: {
          fontSize: 12,
          rotate: 45,
        },
      },
      yAxis: [{
        axisLabel: {
          fontSize: 12,
        },
        min: 0,
        name: 'MWh',
      },
      {
        axisLabel: {
          fontSize: 12,
        },
        min: 0,
        name: 'kW/m²',
        // nameLocation: 'middle',
        // nameGap: 30,
        // splitLine: { show: false },
        position: 'right'
      }],
      series: [
        ...series,
        ugsAverageSeries,
      ],
    };
  });

  const hasData =
    energiaGeradaInversoresChartData.faixa ||
    energiaGeradaInversoresChartData.listaEnergiaGeradaInversor ||
    energiaGeradaInversoresChartData.listaPerformanceUnidadeGeradora;

  return (
    <>
      {hasData ? (
        ugsOptions.map((options, index) => (
          <Grid key={index} sx={{ width: "100%" }} item xs={12}>
            <CardBox
              sx={{
                height: "100%",
                width: "100%",
                minHeight: "400px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Container
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "90%",
                  width: "100%",
                }}
              >
                <RopChart option={options} />
              </Container>
            </CardBox>
          </Grid>
        ))
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <EmptyTable title="Nenhum dado encontrado!" />
        </Box>
      )}
    </>
  );
}
